/**
 * ROUND8: the dog's nose (ROUND8-contracts §1). It smells him outright within `DOGS.noseRadius`, further when the
 * wind carries his scent onto it and less far when the wind carries it away - and by nothing else. Not by sight,
 * so the dark, the ghillie, smoke, a sneak and a trench are all nothing to it (the owner, 2026-09-23: "a trench does
 * not hide him from a dog; water does"). Water between them is the one thing that cuts the scent, as wading is
 * the one thing that wipes his trail (hazards/tracks.ts).
 */

import type { Ctx } from '../../ctx';
import { rangerOnFoot } from '../../hazards/terrain';
import { waterAt } from '../../terrainQueries';
import { DOGS } from '../../tuningDogs';
import type { MissionDef } from '../../types';

/** Metres between the points tested for water on the way from him to the dog: finer than any bank or ford. */
const WATER_STEP = 0.5;
/** Slower than this, m/s, the air is still: no side is downwind of anything. */
const STILL_AIR = 1e-3;

/**
 * How far a dog at (dogX, dogZ) smells a man at (x, z), metres. `wind` is where the wind blows TO. With him straight
 * upwind of the dog his scent blows onto it (x `DOGS.downwind`); with him straight downwind of it, away from it
 * (x `DOGS.upwind`) - the standing order's "STAY DOWNWIND". Across the wind the factor slides between the two with
 * the angle, through the plain radius when the wind is square across; on a still day it is the plain radius.
 */
export function noseReach(wind: MissionDef['wind'], dogX: number, dogZ: number, x: number, z: number): number {
  const speed = Math.sqrt(wind.x * wind.x + wind.z * wind.z);
  const dx = x - dogX;
  const dz = z - dogZ;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (speed < STILL_AIR || d < 1e-6) return DOGS.noseRadius;
  // +1: he is straight downwind of the dog (the wind blows from it to him); -1: straight upwind of it.
  const along = (dx * wind.x + dz * wind.z) / (d * speed);
  const k = along >= 0 ? 1 + along * (DOGS.upwind - 1) : 1 - along * (DOGS.downwind - 1);
  return DOGS.noseRadius * k;
}

/** Open water anywhere on the straight line between the two points, either end included (a pond, a river). */
export function waterBetween(def: MissionDef, ax: number, az: number, bx: number, bz: number): boolean {
  const dx = bx - ax;
  const dz = bz - az;
  const steps = Math.max(1, Math.ceil(Math.sqrt(dx * dx + dz * dz) / WATER_STEP));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    if (waterAt(def, ax + dx * t, az + dz * t)) return true;
  }
  return false;
}

/**
 * Has a dog at (x, z) his scent right now? Out to its nose in this wind, or to `hold` metres if that is further (a
 * dog at bay keeps him: `DOGS.bayHold`) - with no water between them, and only while he is a body on the ground
 * (not under the canopy, not gone). A man wading, or lying under the water, is behind water whichever way it is taken.
 */
export function smellsRanger(ctx: Pick<Ctx, 'world' | 'statics'>, x: number, z: number, hold = 0): boolean {
  const w = ctx.world;
  const r = w.ranger;
  const def = ctx.statics.def;
  if (!rangerOnFoot(w)) return false;
  const reach = Math.max(hold, noseReach(def.wind, x, z, r.x, r.z));
  const dx = r.x - x;
  const dz = r.z - z;
  if (dx * dx + dz * dz > reach * reach) return false;
  return !waterBetween(def, x, z, r.x, r.z);
}
